import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { UploadedImage } from "@/components/image-upload";

export function useImageUpload(uploadSessionId: string) {
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExistingImages = useCallback(async () => {
    if (!uploadSessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("image_uploads")
        .select("id, file_name, file_path, width, height")
        .eq("upload_session_id", uploadSessionId)
        .eq("status", "pending");

      if (fetchError) throw fetchError;

      setUploadedImages(
        (data || []).map((img) => ({
          id: img.id,
          file_name: img.file_name,
          file_path: img.file_path,
          width: img.width,
          height: img.height,
        }))
      );
    } catch (err: any) {
      console.error("Failed to fetch images:", err);
      setError(err.message);
      // Don't clear existing images on fetch error - preserve what we have
    } finally {
      setIsLoading(false);
    }
  }, [uploadSessionId]);

  useEffect(() => {
    fetchExistingImages();
  }, [fetchExistingImages]);

  const addUploadedImages = useCallback((newImages: UploadedImage[]) => {
    setUploadedImages((prev) => [...prev, ...newImages]);
  }, []);

  const removeImage = useCallback(async (imageId: string) => {
    try {
      // Get the image details first
      const imageToRemove = uploadedImages.find((img) => img.id === imageId);
      if (!imageToRemove) return;

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("customer-photos")
        .remove([imageToRemove.file_path]);

      if (storageError) {
        console.error("Storage delete error:", storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from("image_uploads")
        .delete()
        .eq("id", imageId);

      if (dbError) throw dbError;

      setUploadedImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err: any) {
      console.error("Failed to remove image:", err);
      setError(err.message);
    }
  }, [uploadedImages]);

  const getSignedUrl = useCallback(async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from("customer-photos")
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;
    return data.signedUrl;
  }, []);

  return {
    uploadedImages,
    isLoading,
    error,
    addUploadedImages,
    removeImage,
    getSignedUrl,
    refetch: fetchExistingImages,
  };
}
